import React from 'react';
import { Box, Typography, Button as MuiButton, Grid } from '@mui/material';
import * as Icon from 'react-bootstrap-icons';
import { Delete as DeleteIcon } from '@mui/icons-material';

/**
 * 音楽トラックカードコンポーネント
 * 
 * @param {Object} props
 * @param {Object} props.track - トラック情報
 * @param {boolean} props.isPlaying - 再生中かどうか
 * @param {Function} props.onPlay - 再生ボタンクリック時のハンドラ
 * @param {Function} props.onDelete - 削除ボタンクリック時のハンドラ（オプション）
 * @param {boolean} props.showDelete - 削除ボタンを表示するか（デフォルト: false）
 * @param {Function} props.onToggleFavorite - いいねボタンクリック時のハンドラ（オプション）
 * @param {boolean} props.isFavoriteLoading - いいね処理中かどうか（オプション）
 * @param {string} props.size - カードサイズ ('xs' | 'sm' | 'md' | 'lg') デフォルト: 'md'
 */
const TrackCard = ({ 
  track, 
  isPlaying = false, 
  onPlay, 
  onDelete = null, 
  showDelete = false,
  onToggleFavorite = null,
  isFavoriteLoading = false,
  size = 'md'
}) => {
  // サイズ設定
  const sizeConfig = {
    xs: { grid: { xs: 12 }, coverSize: 60, buttonSize: 32, iconSize: 14 },
    sm: { grid: { xs: 12, sm: 6 }, coverSize: 80, buttonSize: 36, iconSize: 16 },
    md: { grid: { xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }, coverSize: 80, buttonSize: 40, iconSize: 18 },
    lg: { grid: { xs: 12, sm: 6, md: 4 }, coverSize: 120, buttonSize: 48, iconSize: 24 }
  };

  const config = sizeConfig[size] || sizeConfig.md;

  return (
    <Box sx={{ 
      border: '1px solid', 
      borderColor: isPlaying ? 'primary.main' : 'divider',
      borderRadius: 2,
      overflow: 'hidden',
      bgcolor: 'background.paper',
      transition: 'all 0.2s',
      height: '100%',
      display: 'flex',
      flexDirection: size === 'sm' || size === 'xs' ? { xs: 'row', sm: 'column' } : 'column',
      '&:hover': { 
        boxShadow: 3,
        transform: 'translateY(-2px)'
      }
    }}>
        {/* カバーアート */}
        <Box sx={{ 
          position: 'relative',
          width: (size === 'sm' || size === 'xs') ? { xs: config.coverSize, sm: '100%' } : '100%',
          paddingTop: (size === 'sm' || size === 'xs') ? { xs: 0, sm: '100%' } : '100%',
          height: (size === 'sm' || size === 'xs') ? { xs: config.coverSize, sm: 'auto' } : 'auto',
          bgcolor: 'grey.200',
          overflow: 'hidden',
          flexShrink: 0
        }}>
          {(track.cover_art_url_small || track.cover_art_url) ? (
            <picture style={{ 
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%', 
              height: '100%'
            }}>
              {/* WebP画像をレスポンシブに使用 */}
              {track.cover_art_url_large && (
                <source 
                  media="(min-width: 768px)" 
                  srcSet={track.cover_art_url_medium} 
                  type="image/webp" 
                />
              )}
              {track.cover_art_url_small && (
                <source 
                  srcSet={track.cover_art_url_small} 
                  type="image/webp" 
                />
              )}
              {/* フォールバック: 元画像 */}
              <img 
                src={track.cover_art_url} 
                alt={track.title} 
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'cover' 
                }} 
              />
            </picture>
          ) : (
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Icon.MusicNoteBeamed size={config.coverSize / 2} color="#999" />
            </Box>
          )}
          
          {/* 再生ボタンオーバーレイ */}
          {track.audio_file_url && onPlay && (
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: { xs: 'flex', sm: 'flex' },
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(0,0,0,0.3)',
              opacity: { xs: 1, sm: isPlaying ? 1 : 0 },
              transition: 'opacity 0.2s',
              '&:hover': { opacity: 1 }
            }}>
              <MuiButton
                variant="contained"
                onClick={onPlay}
                sx={{ 
                  minWidth: 'auto', 
                  width: config.buttonSize,
                  height: config.buttonSize,
                  p: 0,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  '&:hover': { bgcolor: 'primary.dark' }
                }}
              >
                {isPlaying ? (
                  <Icon.PauseFill size={config.iconSize} />
                ) : (
                  <Icon.PlayFill size={config.iconSize} />
                )}
              </MuiButton>
            </Box>
          )}
        </Box>
        
        {/* 情報エリア */}
        <Box sx={{ 
          p: 1.5, 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          minWidth: 0
        }}>
          <Typography 
            variant="subtitle2" 
            sx={{ 
              fontWeight: 600, 
              mb: 0.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: size === 'sm' || size === 'xs' ? { xs: 'block', sm: '-webkit-box' } : '-webkit-box',
              WebkitLineClamp: (size === 'sm' || size === 'xs') ? { xs: 1, sm: 2 } : 2,
              WebkitBoxOrient: 'vertical',
              minHeight: (size === 'sm' || size === 'xs') ? { xs: 'auto', sm: '2.5em' } : '2.5em'
            }}
          >
            {track.title}
          </Typography>
          
          {track.album_title && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ mb: { xs: 0.25, sm: 0.5 } }}>
              <Icon.CollectionPlay size={12} style={{ marginRight: 2, verticalAlign: 'text-bottom' }} />
              {track.album_title}
            </Typography>
          )}
          
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1, alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <Icon.PlayCircle size={12} />
              {track.view_count || 0}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <Icon.HeartFill size={12} />
              {track.favorite_count || 0}
            </Typography>
            {track.duration && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                <Icon.Clock size={12} />
                {Math.floor(track.duration / 60)}:{String(track.duration % 60).padStart(2, '0')}
              </Typography>
            )}
            
            {/* いいねボタン */}
            {onToggleFavorite && (
              <MuiButton
                size="small"
                disabled={isFavoriteLoading}
                onClick={onToggleFavorite}
                sx={{
                  minWidth: 'auto',
                  p: 0.5,
                  ml: 'auto',
                  color: track.is_favorited ? 'error.main' : 'text.secondary'
                }}
              >
                {track.is_favorited ? <Icon.HeartFill size={16} /> : <Icon.Heart size={16} />}
              </MuiButton>
            )}
          </Box>
          
          {/* 削除ボタン */}
          {showDelete && onDelete && (
            <MuiButton 
              size="small" 
              color="error"
              variant="text"
              startIcon={null}
              onClick={onDelete}
              sx={{ 
                mt: 'auto',
                minWidth: 'auto',
                px: 1
              }}
            >
              <DeleteIcon fontSize="small" />
            </MuiButton>
          )}
        </Box>
    </Box>
  );
};

export default TrackCard;
